'use client'

import { useSubscriptionVideosQuery } from '@/src/queries/subscriptions/subscription-videos'
import React, { useEffect } from 'react'
import { Box, Flex, Text, Image } from '@chakra-ui/react'
import { useInView } from 'react-intersection-observer'
import VidGrid from '@/components/ui/video-grid'
import { LoadingVideoCard, VideoCard } from '@/components/ui/video-card'
import NoSubscriptions from './none/NoPage'

export default function SubscriptionsFeed({
  setNoSubscriptions,
}: {
  setNoSubscriptions: (nosub: boolean) => void
}) {
  const { ref, inView } = useInView()

  const result = useSubscriptionVideosQuery()
  const {
    status,
    data,
    isFetchingNextPage,
    isFetching,
    fetchNextPage,
    hasNextPage,
  } = result
  const hasNextPageRef = React.useRef<boolean>(hasNextPage)

  if (setNoSubscriptions && typeof setNoSubscriptions == 'function') {
    if (data?.pages.flatMap((page) => page.videos).length === 0) {
      setNoSubscriptions(true)
    } else {
      setNoSubscriptions(false)
    }
  }

  useEffect(() => {
    hasNextPageRef.current = hasNextPage
  }, [hasNextPage])

  useEffect(() => {
    if (
      inView &&
      hasNextPageRef.current &&
      !isFetchingNextPage &&
      !isFetching
    ) {
      fetchNextPage()
    }
  }, [inView, isFetchingNextPage, fetchNextPage, isFetching])

  let GridContent: React.ReactNode = null
  if (status == 'pending') {
    GridContent = (
      <>
        {Array(5)
          .fill('-')
          .map((_, i) => (
            <LoadingVideoCard key={i} />
          ))}
      </>
    )
  }

  if (data?.pages.flatMap((page) => page.videos).length === 0) {
    return <NoSubscriptions />
  }

  if (status == 'success') {
    GridContent = data?.pages.map((page, i) => (
      <React.Fragment key={i}>
        {page.videos.map((video) => (
          <VideoCard
            key={video.eventId}
            eventId={video.eventId}
            creatorId={video.channelId}
            videoDuration={video.duration}
            avatarUrl={video.imageUrl}
            creatorName={video.channelName}
            title={video.eventTitle}
            watchTime={video.UniqueWatchTime}
            thumbnailUrl={video.thumbnailImage}
            sharedImageName={video.sharedImageName || video.thumbnailImage}
            timelineUpload={video.eventEntryDate}
            live={video.type == '2' ? 'live' : ''}
            isWatchList={video.isWatchList}
            encryptVideoUrl={video.encryptVideoUrl}
            themeColor={video.themeColor || '#E79051'}
            channelShareName={video.channelShareName}
            isVerifiedTick={video.isVerifiedTick}
            watchCount={video.watchCount || '0'}
          />
        ))}
      </React.Fragment>
    ))
  }

  if (status == 'error') {
    return (
      <Flex
        w="100%"
        flex="1"
        flexDir="column"
        align="center"
        justify="center"
        mt={40}
      >
        <Image src="/failed.svg" width={150} height={150} alt="Failed Icon" />
        <Text mt={4} color="black" fontWeight={600} fontSize={18}>
          Failed to load videos!
        </Text>
      </Flex>
    )
  }

  return (
    <Box my={6}>
      <VidGrid>
        {GridContent}
        {isFetchingNextPage &&
          Array(4)
            .fill('-')
            .map((_, i) => <LoadingVideoCard key={i} />)}
      </VidGrid>
      <div ref={ref} className="pb-10"></div>
    </Box>
  )
}
